import type { FC } from "react";
import { mergeClasses, truncateText } from "@huub-web/utils";

import { Text } from "../Text";
import { useFormattedDate } from "../../hooks/useFormattedDate";
import type { ChatMessage } from "../../types";
import { Flex } from "../Flex";
import type { ClassNameProp } from "../../props";
import type { ChatViewVariant } from "../ChatView";
import { Avatar } from "../Avatar";
import { DebugLabel } from "../DebugLabel";

import { visualDebugContainerStyle } from "../../theme/css/visualDebugContainer.css";
import { bodyStyle, containerStyle, timeStyle } from "./UserMessage.css";

export type UserMessageProps = ClassNameProp & {
  message: ChatMessage;
  variant?: ChatViewVariant;
  showTime?: boolean;
  showAuthor?: boolean;
  debug?: boolean;
};

/**
 * A user message component that displays a message with an icon.
 * @todo: Translate
 */
export const UserMessage: FC<UserMessageProps> = ({
  message,
  variant = "default",
  showTime = false,
  showAuthor = false,
  debug = false,
  className,
}) => {
  const { formatDate } = useFormattedDate();

  const displayBody = (() => {
    if (!message.content) {
      console.warn("UserMessage: message.content is undefined", message);
      return "";
    }
    return message.content.trim().replace(/\n/g, "");
  })();

  const displayTime = formatDate(message.created, { format: "minutesHours" });

  const displayAuthor = (() => {
    if (message.author === "agent") {
      return undefined;
    }

    if (message.author.type === "human") {
      if (message.author.name) {
        return truncateText(message.author.name, 20);
      }
      if (message.author.email) {
        return truncateText(message.author.email, 20);
      }
      return "Jij";
    }

    if (message.author.type === "advisor") {
      if (message.author.name) {
        return truncateText(message.author.name, 20);
      }
      return "Adviseur";
    }

    return undefined;
  })();

  return (
    <Flex
      data-id={message.id}
      data-component="UserMessage"
      direction="column"
      gap="xs"
      className={mergeClasses(
        debug ? visualDebugContainerStyle : undefined,
        className,
      )}
    >
      {debug && message.context && (
        <DebugLabel>{message.context.join(", ")}</DebugLabel>
      )}

      {showAuthor && (
        <Flex gap="xs" align="center" width="stretch">
          <Avatar
            id={displayAuthor}
            size="xs"
            value={displayAuthor}
            variant="user"
          />
          <Text variant="bodyS" color="currentColor50">
            {displayAuthor}
          </Text>
        </Flex>
      )}

      <div className={mergeClasses(containerStyle({ arrow: true, variant }))}>
        <Text align="left" className={bodyStyle}>
          {displayBody}
        </Text>

        {showTime && (
          <Text variant="bodyXS" color="currentColor50" className={timeStyle}>
            {displayTime}
          </Text>
        )}
      </div>
    </Flex>
  );
};
